import React, { useState, useEffect } from 'react';
import { Box, VStack, Text, Spinner, Center, Badge, HStack, Flex } from '@chakra-ui/react';
import { authAPI } from '../services/api';
import { formatDateTime } from '../utils/dateUtils'; // Assuming this exists, or use local helper

const DashboardLeavesWidget = () => {
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLeaves = async () => {
            try {
                // Fetch leaves for today.
                // Depending on backend, we might need specific params. 
                // Assuming default getLeaves returns all, we might filter client side or pass params.
                // Ideally backend supports date filtering. Let's try basic fetch first.
                // IMPORTANT: In a real app, use query params like ?start_date=...
                // Use API filtering for today
                // Use local date (YYYY-MM-DD) instead of UTC to avoid timezone issues
                // const today = new Date().toISOString().split('T')[0]; // This is UTC!
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const today = `${year}-${month}-${day}`;

                // Fix: Pass both start_date and end_date to exact match today.
                // Previously, only start_date was used, and backend sorts DESC (future first).
                // If many future leaves exist, limit=20 would exclude today's leaves.
                const res = await authAPI.leaves.getLeaves({
                    start_date: today,
                    end_date: today,
                    limit: 100
                });

                setLeaves(res.data);
            } catch (error) {
                console.error("Failed to fetch leaves", error);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaves();
    }, []);

    if (loading) {
        return <Center p={4}><Spinner size="sm" /></Center>;
    }

    if (leaves.length === 0) {
        return (
            <Center p={4} flexDirection="column">
                <Text fontSize="sm" color="gray.500">今日無請假紀錄</Text>
            </Center>
        );
    }

    return (
        <Box
            h="100%"
            overflowY="auto"
            pr={2}
            sx={{
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                '&::-webkit-scrollbar-thumb': { background: '#A0AEC0', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: '#718096' },
            }}
        >
            <VStack align="stretch" spacing={0} divider={<Box borderBottomWidth="1px" borderColor="gray.100" />}>
                {leaves.map((leave) => (
                    <Box key={leave.id} p={3} _hover={{ bg: "gray.50" }}>
                        <Flex justify="space-between" align="start">
                            <Box>
                                <HStack spacing={2} align="baseline">
                                    <Text fontWeight="bold" fontSize="sm">{leave.student?.full_name}</Text>
                                    <Text fontSize="xs" color="gray.500">
                                        {(() => {
                                            const s = leave.student;
                                            if (!s) return '';
                                            // Combine primary and secondary classes
                                            const allClasses = [...(s.classes || [])];
                                            if (s.class) allClasses.push(s.class);

                                            // Priority 1: AnChin (AfterSchool or Name with '全科班')
                                            const anChin = allClasses.find(c => (c.department === 'AfterSchool' || (c.name && c.name.includes('全科班'))));
                                            if (anChin) return anChin.name;

                                            // Priority 2: English (English or Name with '英文')
                                            const english = allClasses.find(c => (c.department === 'English' || (c.name && c.name.includes('英文'))));
                                            if (english) return english.name;

                                            // Fallback
                                            return s.class?.name || (allClasses.length > 0 ? allClasses[0].name : '');
                                        })()}
                                    </Text>
                                </HStack>
                                <Text fontSize="xs" color="gray.500">{leave.reason}</Text>
                            </Box>
                            <Badge
                                colorScheme={leave.type === 'SICK' ? 'red' : leave.type === 'PERSONAL' ? 'blue' : 'gray'}
                                fontSize="xs"
                                variant="subtle"
                            >
                                {leave.type === 'SICK' ? '病假' : leave.type === 'PERSONAL' ? '事假' : '其他'}
                            </Badge>
                        </Flex>
                    </Box>
                ))}
            </VStack>
        </Box>
    );
};

export default DashboardLeavesWidget;
